import {
  Button,
  Flex,
  Group,
  Modal,
  Radio,
  Stack,
  Text,
  Alert,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications"; // Import the useNotifications hook
import React, { useState, useEffect } from "react";
import { useUpdateTeamMemberCredits } from "../hooks/useUpdateTeamMemberCredits";
import { RETURN_LABEL } from "../constants";

interface EditAccessTypeModalProps {
  opened: boolean;
  onClose: () => void;
  teamMember: {
    allocatedCredits: number;
    businessUse: boolean;
    userId: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    status: string;
  } | null;
  signupSource?: string;
}

const EditAccessTypeModal = ({
  opened,
  onClose,
  teamMember,
  signupSource,
}: EditAccessTypeModalProps): React.ReactElement => {
  const [accessType, setAccessType] = useState<string | null>(null);

  // Check if this is a pay-later business (metered or pay_by_refund)
  const isPayLater =
    signupSource === "metered" || signupSource === "pay_by_refund";

  // Auto-select Business Use for pay-later businesses
  useEffect(() => {
    if (opened && isPayLater) {
      setAccessType("Business Use");
    }
  }, [opened, isPayLater]);

  // Use the mutation hook
  const { mutate, isPending, isError } = useUpdateTeamMemberCredits();

  const accessOptions = [
    {
      name: "Limited Use",
      description: `This member has a fixed ${RETURN_LABEL} balance assigned to them. They can only use the ${RETURN_LABEL}s that have been directly allocated to them.`,
      disabled: isPayLater,
    },
    {
      name: "Business Use",
      description: `This member will be given access to the business ${RETURN_LABEL} balance. Any remaining allocated ${RETURN_LABEL}s will be added back to the business balance.`,
      disabled: false,
    },
  ];

  const renderAccessOptions = accessOptions.map((option) => (
    <Radio.Card
      radius="md"
      value={option.name}
      key={option.name}
      disabled={option.disabled}
      style={{
        opacity: option.disabled ? 0.5 : 1,
        cursor: option.disabled ? "not-allowed" : "pointer",
      }}
    >
      <Group wrap="nowrap" align="flex-start" p="xs">
        <Radio.Indicator />
        <div>
          <Text fw={500}>{option.name}</Text>
          <Text c="dimmed" size="sm">
            {option.description}
          </Text>
          {option.disabled && (
            <Text c="red" size="xs" mt={4}>
              Not available for your billing plan
            </Text>
          )}
        </div>
      </Group>
    </Radio.Card>
  ));

  const handleSave = (): void => {
    if (!teamMember || !accessType) return;

    // Prepare the payload for the mutation
    const payload = {
      target_user_id: teamMember.userId,
      business_use: accessType === "Business Use", // Set business_use based on the selected access type
      credit_request: false, // Optional: Set credit_request if needed
    };

    // Trigger the mutation
    mutate(payload, {
      onSuccess: () => {
        // Show success notification
        showNotification({
          title: "Success",
          message: `Team member ${RETURN_LABEL}s updated successfully.`,
          color: "green",
        });
        // Close the modal on success
        onClose();
      },
      onError: () => {
        // Show error notification
        showNotification({
          title: "Error",
          message: `Failed to update team member ${RETURN_LABEL}s. Please try again.`,
          color: "red",
        });
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Edit Team Member Access</Text>}
      size="lg"
      radius={"md"}
      padding={"lg"}
    >
      <Stack gap="md">
        <Group gap="xs">
          <Text c="dimmed" size="sm">
            Team Member:{" "}
            <strong style={{ color: "#005fa3" }}>
              {teamMember?.firstName} {teamMember?.lastName}
            </strong>
          </Text>
        </Group>
        <div>
          <Text fw={500}>{RETURN_LABEL} Access Type</Text>
          <Text c="dimmed" size="sm">
            Select how this team member can use business{" "}
            {RETURN_LABEL.toLowerCase()}s
          </Text>
        </div>

        {isPayLater && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm">
              Your business is on a metered/pay-by-refund plan. Team members
              must have <strong>Business Use</strong> access to automatically
              bill usage to your account.
            </Text>
          </Alert>
        )}

        <Radio.Group
          mb={"md"}
          value={accessType}
          onChange={setAccessType}
          label="Select an access type"
          description={`Choose how this team member will use ${RETURN_LABEL.toLowerCase()}s`}
        >
          <Stack pt="sm" gap="xs">
            {renderAccessOptions}
          </Stack>
        </Radio.Group>
      </Stack>

      {/* Display error message if mutation fails */}
      {isError && (
        <Text c="red" size="sm" mb="md">
          Failed to update team member {RETURN_LABEL.toLowerCase()}s. Please try
          again.
        </Text>
      )}

      <Flex justify="flex-end">
        <Button
          onClick={handleSave}
          disabled={accessType === null || isPending} // Disable the button if no access type is selected or the mutation is loading
          loading={isPending} // Show a loading spinner on the button
        >
          Save
        </Button>
      </Flex>
    </Modal>
  );
};

export default EditAccessTypeModal;
